import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  downloadProjectLandingPdfById,
  downloadPublicProjectLandingPdf,
  fetchProjectLandingById,
  fetchPublicProjectLanding,
  fetchPublicProjectLandingDocumentSigned,
  getProjectOverviewPdfSignedUrl,
} from "../services/investmentService";
import { useAuth } from "../store/AuthContext";
import { getProjectLifecycleLabel } from "../utils/projectViews";
import { projectVideoEmbedSrc, splitDescriptionParagraphs } from "../utils/projectDescription";

import "../project-landing.css";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatMoneyBdt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2 }).format(n);
  } catch {
    return String(value);
  }
}

/** Render lightweight Markdown emphasis: **bold** → <strong>. */
function renderBoldText(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

/**
 * Lightweight Markdown for risk disclosure: headings, bullets, **bold**.
 * Markers are not shown; only formatted text is.
 */
function renderRiskMarkdown(text) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let listItems = [];
  let paraLines = [];

  const flushPara = () => {
    if (!paraLines.length) return;
    const body = paraLines.join(" ").trim();
    paraLines = [];
    if (!body) return;
    blocks.push({ type: "p", body });
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: "ul", items: listItems });
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (/^#{1,6}\s+/.test(line)) {
      flushPara();
      flushList();
      // Section already has an h2; skip duplicate markdown titles like "# RISK DISCLOSURE"
      continue;
    }
    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      flushPara();
      listItems.push(bullet[1]);
      continue;
    }
    flushList();
    paraLines.push(line);
  }
  flushPara();
  flushList();

  return blocks.map((block, bi) => {
    if (block.type === "ul") {
      return (
        <ul key={`risk-ul-${bi}`} className="nh-pl__risk-list">
          {block.items.map((item, ii) => (
            <li key={`risk-li-${bi}-${ii}`}>{renderBoldText(item)}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={`risk-p-${bi}`} className="nh-pl__risk">
        {renderBoldText(block.body)}
      </p>
    );
  });
}

export default function ProjectLandingPage({ byId = false }) {
  const { slug, projectId } = useParams();
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [docBusy, setDocBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = byId
          ? await fetchProjectLandingById(projectId)
          : await fetchPublicProjectLanding(slug);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err.response?.data?.detail || "Landing page not found.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [byId, projectId, slug]);

  useEffect(() => {
    if (!data?.name) return undefined;
    const prev = document.title;
    document.title = `${data.name} · NeedHealth.Care`;
    return () => {
      document.title = prev;
    };
  }, [data?.name]);

  const openProjectPdf = async () => {
    setDocBusy(true);
    setError("");
    try {
      const publicSlug = data?.slug || slug;
      if (publicSlug) {
        const { data: signed } = await fetchPublicProjectLandingDocumentSigned(publicSlug);
        if (signed?.url) {
          window.open(signed.url, "_blank", "noopener,noreferrer");
          return;
        }
      }
      const id = data?.id || projectId;
      if (!id) throw new Error("missing project");
      const { data: signed } = await getProjectOverviewPdfSignedUrl(id);
      if (signed?.url) window.open(signed.url, "_blank", "noopener,noreferrer");
      else throw new Error("no url");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not open project PDF.");
    } finally {
      setDocBusy(false);
    }
  };

  const downloadPagePdf = async () => {
    setPdfBusy(true);
    setError("");
    try {
      if (byId && projectId) {
        await downloadProjectLandingPdfById(projectId, data?.slug || data?.project_code || "project");
      } else if (data?.slug || slug) {
        await downloadPublicProjectLandingPdf(data?.slug || slug);
      } else if (data?.id) {
        await downloadProjectLandingPdfById(data.id, data?.project_code || "project");
      } else {
        throw new Error("missing landing");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not download landing PDF.");
    } finally {
      setPdfBusy(false);
    }
  };

  const copyShare = async () => {
    const url = data?.share_url || (data?.share_path ? `${window.location.origin}${data.share_path}` : "");
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* ignore */
    }
  };

  const paragraphs = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data.about_paragraphs) && data.about_paragraphs.length) {
      return data.about_paragraphs.map((p) => String(p || "").trim()).filter(Boolean);
    }
    return splitDescriptionParagraphs(data.about).filter(Boolean);
  }, [data]);

  const videoEmbed = useMemo(() => projectVideoEmbedSrc(data?.video_url), [data?.video_url]);

  if (loading) {
    return (
      <div className="nh-pl">
        <p className="nh-pl__muted" style={{ padding: 24 }}>
          Loading…
        </p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="nh-pl">
        <p className="nh-pl__error">{error}</p>
        <Link to="/">Back to home</Link>
      </div>
    );
  }

  const terms = data.terms || {};
  const investTo = accessToken ? `/investments?project_id=${data.id}` : "/login";
  const projectName = String(data.name || "").trim();
  const projectAddress = String(data.address || "").trim();
  const hasProjectPdf = Boolean(data.has_overview_pdf);
  const hasVideo = Boolean(String(data.video_url || "").trim());
  const statusLabel = getProjectLifecycleLabel(data.status) || String(data.status || "").replaceAll("_", " ");

  return (
    <div className="nh-pl">
      <header className="nh-pl__top">
        <Link to="/" className="nh-pl__brand">
          NeedHealth<span>.Care</span>
        </Link>
        <div className="nh-pl__top-actions">
          {data.share_path ? (
            <button type="button" className="nh-pl__btn nh-pl__btn--ghost" onClick={copyShare}>
              Copy share link
            </button>
          ) : null}
          {!user ? (
            <Link to="/login" className="nh-pl__btn">
              Sign in
            </Link>
          ) : null}
        </div>
      </header>

      <section className={`nh-pl__hero${data.hero_image_url ? "" : " nh-pl__hero--empty"}`} aria-label="Project banner">
        {data.hero_image_url ? (
          <img className="nh-pl__hero-img" src={data.hero_image_url} alt="" />
        ) : null}
        {!data.published ? <p className="nh-pl__draft nh-pl__draft--hero">Draft preview — not published</p> : null}
      </section>

      <main className="nh-pl__main">
        {error ? <p className="nh-pl__error">{error}</p> : null}

        <header className="nh-pl__title-block">
          {data.project_code ? <p className="nh-pl__code">{data.project_code}</p> : null}
          <h1 className="nh-pl__project-name">{projectName || "Project"}</h1>
          {projectAddress ? <p className="nh-pl__project-address">{projectAddress}</p> : null}
        </header>

        <section className="nh-pl__section" aria-label="Offer facts">
          <dl className="nh-pl__facts">
            <div>
              <dt>Total shares</dt>
              <dd>{terms.total_shares || "—"}</dd>
            </div>
            <div>
              <dt>Price per share</dt>
              <dd>৳ {formatMoneyBdt(terms.initial_share_price)}</dd>
            </div>
            <div>
              <dt>Selling start</dt>
              <dd>{formatDate(terms.fundraising_start)}</dd>
            </div>
            <div>
              <dt>Selling end</dt>
              <dd>{formatDate(terms.fundraising_end)}</dd>
            </div>
            <div>
              <dt>Project status</dt>
              <dd>{statusLabel || "—"}</dd>
            </div>
          </dl>
        </section>

        <section className="nh-pl__section nh-pl__profit">
          <h2>Estimated profit</h2>
          <p className="nh-pl__profit-value">
            {terms.expected_return_pct != null ? `${terms.expected_return_pct}%` : "—"}
            <span> per annum</span>
          </p>
        </section>

        <section className="nh-pl__section">
          <h2>Project description</h2>
          {paragraphs.length ? (
            <div className="nh-pl__about-stack">
              {paragraphs.map((para, idx) => (
                <p key={`about-${idx}`} className="nh-pl__about">
                  {para}
                </p>
              ))}
            </div>
          ) : (
            <p className="nh-pl__about">No description yet.</p>
          )}
        </section>

        {hasProjectPdf || hasVideo ? (
          <section className="nh-pl__section nh-pl__section--media" aria-labelledby="nh-pl-media-heading">
            <h2 id="nh-pl-media-heading">Media</h2>
            <div className="nh-pl__media-grid">
              {hasProjectPdf ? (
                <div className="nh-pl__media-card">
                  <h3>Project PDF</h3>
                  <button type="button" className="nh-pl__btn nh-pl__btn--primary" onClick={openProjectPdf} disabled={docBusy}>
                    {docBusy ? "Opening…" : "Download project PDF"}
                  </button>
                </div>
              ) : null}
              {hasVideo ? (
                <div className="nh-pl__media-card nh-pl__media-card--video">
                  <h3>Project video</h3>
                  {videoEmbed ? (
                    <div className="nh-pl__video-frame">
                      <iframe
                        title="Project video"
                        src={videoEmbed}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <a className="nh-pl__btn" href={data.video_url} target="_blank" rel="noopener noreferrer">
                      Open video link
                    </a>
                  )}
                </div>
              ) : null}
            </div>
            <button type="button" className="nh-pl__btn nh-pl__btn--ghost nh-pl__page-pdf" onClick={downloadPagePdf} disabled={pdfBusy}>
              {pdfBusy ? "Preparing…" : "Download landing page PDF"}
            </button>
          </section>
        ) : (
          <section className="nh-pl__section">
            <button type="button" className="nh-pl__btn nh-pl__btn--ghost" onClick={downloadPagePdf} disabled={pdfBusy}>
              {pdfBusy ? "Preparing…" : "Download landing page PDF"}
            </button>
          </section>
        )}

        {data.risk_disclaimer ? (
          <section className="nh-pl__section nh-pl__section--risk">
            <h2>Risk disclosure</h2>
            <div className="nh-pl__risk-body">{renderRiskMarkdown(data.risk_disclaimer)}</div>
          </section>
        ) : null}

        <section className="nh-pl__cta">
          <button type="button" className="nh-pl__btn nh-pl__btn--primary nh-pl__btn--buy" onClick={() => navigate(investTo)}>
            {accessToken ? "Buy shares" : "Sign in to buy shares"}
          </button>
        </section>
      </main>
    </div>
  );
}
